// src/components/layout/Navbar.jsx - UPDATED WITH REAL-TIME PRODUCT REQUESTS (Using NotificationContext)
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext'; // ADD THIS IMPORT
import { collection, query, where, orderBy, getDocs, limit, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

const Navbar = ({ onMobileMenuToggle }) => {
  const { currentUser, vendorProfile, logout } = useAuth();
  const { productRequests, getPendingProductRequestsCount } = useNotification(); // ADD THIS LINE
  const navigate = useNavigate();

  // Combined state for both notifications and product requests
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const profileMenuRef = useRef(null);
  const notificationsRef = useRef(null);

  // Check if vendor has order management
  const hasOrderManagement = vendorProfile?.businessType !== 'without-orders';

  // Note: Real-time product requests are handled by NotificationContext
  // No need for separate listener here - prevents duplicate listeners and Firebase errors

  // UPDATED: Load notifications with real-time product requests from NotificationContext
  const loadAllNotifications = async () => {
    if (!currentUser?.uid) {
      console.log('❌ No current user for notifications');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Loading all notifications for vendor:', currentUser.uid);
      console.log('🔍 Navbar: productRequests from context:', productRequests ? productRequests.length : 'null/undefined');
      console.log('🔍 Navbar: hasOrderManagement:', hasOrderManagement);

      const combinedNotifications = [];
      let totalUnread = 0;

      // 1. Load regular notifications (if collection exists)
      try {
        const notificationRef = collection(db, 'notifications');
        const notifQuery = query(
          notificationRef,
          where('vendorId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(20)
        );

        const notifSnapshot = await getDocs(notifQuery);
        
        notifSnapshot.forEach((doc) => {
          const notification = {
            id: doc.id,
            source: 'notifications', // Mark source
            ...doc.data()
          };
          combinedNotifications.push(notification);
          
          if (!notification.read) {
            totalUnread++;
          }
        });

        console.log('✅ Loaded regular notifications:', notifSnapshot.size);
      } catch (notifError) {
        console.log('ℹ️ No notifications collection or access:', notifError.message);
      }

      // 2. UPDATED: Use product requests from NotificationContext
      console.log('🔄 Checking product requests...', {
        hasOrderManagement,
        productRequestsExists: !!productRequests,
        productRequestsLength: productRequests ? productRequests.length : 0
      });

      if (hasOrderManagement && productRequests && productRequests.length > 0) {
        try {
          const recentRequests = productRequests.slice(0, 10);
          console.log('📦 Processing product requests in Navbar:', recentRequests.length);

          recentRequests.forEach((request) => {
            // Convert product request to notification format
            const notification = {
              id: `request_${request.id}`,
              source: 'productRequests', // Mark source
              originalId: request.id, // Keep original ID for actions
              type: 'product_request',
              title: 'New Product Request',
              message: `${request.mechanicName || 'A mechanic'} requested ${request.requestedQuantity || 1} x ${request.productName || 'product'}`,
              createdAt: request.createdAt,
              expiresAt: request.expiresAt,
              read: request.status !== 'pending', // Pending = unread
              productName: request.productName,
              mechanicName: request.mechanicName,
              requestedQuantity: request.requestedQuantity,
              status: request.status
            };

            combinedNotifications.push(notification);
            
            // Count pending requests as unread
            if (request.status === 'pending' && !isExpired(request)) {
              totalUnread++;
            }
          });

          console.log('✅ Added product requests from NotificationContext:', recentRequests.length);
        } catch (requestError) {
          console.error('❌ Error processing product requests:', requestError);
        }
      } else {
        console.log('⚠️ No product requests to process:', {
          hasOrderManagement,
          productRequestsExists: !!productRequests,
          productRequestsLength: productRequests ? productRequests.length : 0
        });
      }

      // 3. Sort combined notifications by date
      combinedNotifications.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA; // Newest first
      });

      // 4. Take only the most recent 10 for dropdown
      const recentNotifications = combinedNotifications.slice(0, 10);

      console.log('✅ Combined notifications:', recentNotifications.length, 'unread:', totalUnread);
      setAllNotifications(recentNotifications);
      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('❌ Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if request is expired
  const isExpired = (request) => {
    if (!request.expiresAt) return false;
    
    try {
      const now = new Date();
      const expiry = request.expiresAt.toDate ? request.expiresAt.toDate() : new Date(request.expiresAt);
      return now > expiry;
    } catch (error) {
      return false;
    }
  };

  // UPDATED: Load notifications when component mounts, user changes, or product requests change
  useEffect(() => {
    console.log('🔄 Navbar useEffect triggered:', {
      currentUserId: currentUser?.uid,
      hasOrderManagement,
      productRequestsLength: productRequests ? productRequests.length : 'null/undefined'
    });
    
    if (currentUser?.uid) {
      loadAllNotifications();
    } else {
      setAllNotifications([]);
      setUnreadCount(0);
    }
  }, [currentUser?.uid, hasOrderManagement, productRequests]); // ADD productRequests dependency

  // Mark notification as read
  const markAsRead = async (notification) => {
    try {
      if (notification.source === 'notifications') {
        // Handle regular notification
        const notificationRef = doc(db, 'notifications', notification.id);
        await updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp()
        });
      } else if (notification.source === 'productRequests') {
        // Product requests don't have a "read" field, they're considered read when status changes
        // We'll just update local state for UI purposes
        console.log('Product request marked as viewed:', notification.originalId);
      }
      
      // Update local state immediately
      setAllNotifications(prev => 
        prev.map(notif => 
          notif.id === notification.id 
            ? { ...notif, read: true }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
      console.log('✅ Notification marked as read:', notification.id);
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      console.log('🔄 Marking all notifications as read...');
      
      for (const notification of allNotifications) {
        if (!notification.read && notification.source === 'notifications') {
          const notificationRef = doc(db, 'notifications', notification.id);
          await updateDoc(notificationRef, {
            read: true,
            readAt: serverTimestamp()
          });
        }
      }
      
      // Update local state
      setAllNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
      
      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Format notification time
  const formatNotificationTime = (timestamp) => {
    if (!timestamp) return 'Recently';

    try {
      const now = new Date();
      const notificationTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const diffTime = Math.abs(now - notificationTime);
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffTime / (1000 * 60));

      if (diffHours > 0) {
        return `${diffHours}h ago`;
      } else if (diffMinutes > 0) {
        return `${diffMinutes}m ago`;
      } else {
        return 'Just now';
      }
    } catch (error) {
      console.error('Error formatting time:', error);
      return 'Recently';
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    console.log('🔔 Notification clicked:', notification);
    
    try {
      // Mark as read if unread
      if (!notification.read) {
        await markAsRead(notification);
      }

      // Close the dropdown
      setShowNotifications(false);

      // Navigate based on notification type and source
      if (notification.type === 'product_request' || notification.type === 'product-request') {
        navigate('/orders?tab=product-requests');
      } else if (notification.type === 'order' && notification.orderId) {
        navigate(`/orders/${notification.orderId}`);
      } else if (notification.type === 'message' && notification.chatId) {
        navigate(`/communication/${notification.chatId}`);
      } else if (notification.type === 'product' && notification.productId) {
        navigate(`/inventory/${notification.productId}`);
      } else if (notification.type === 'customer' && notification.customerId) {
        navigate(`/customers/${notification.customerId}`);
      } else {
        navigate('/notifications');
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    const iconClass = "h-5 w-5";
    const containerClass = "w-10 h-10 rounded-full flex items-center justify-center";

    switch (type) {
      case 'product_request':
      case 'product-request':
        return (
          <div className={`${containerClass} bg-blue-100`}>
            <svg className={`${iconClass} text-blue-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
        );
      case 'order':
        return (
          <div className={`${containerClass} bg-green-100`}>
            <svg className={`${iconClass} text-green-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
        );
      case 'message':
        return (
          <div className={`${containerClass} bg-orange-100`}>
            <svg className={`${iconClass} text-orange-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className={`${containerClass} bg-gray-100`}>
            <svg className={`${iconClass} text-gray-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  // Get priority badge
  const getPriorityBadge = (notification) => {
    if (notification.type === 'product_request' && notification.expiresAt) {
      try {
        const now = new Date();
        const expiry = notification.expiresAt.toDate ? notification.expiresAt.toDate() : new Date(notification.expiresAt);
        const hoursLeft = (expiry - now) / (1000 * 60 * 60);
        
        if (hoursLeft <= 0) {
          return <span className="text-xs text-red-600 font-medium">Expired</span>;
        } else if (hoursLeft <= 1) {
          return <span className="text-xs text-red-600 font-medium">Urgent</span>;
        } else if (hoursLeft <= 6) {
          return <span className="text-xs text-yellow-600 font-medium">Soon</span>;
        }
      } catch (error) {
        console.error('Error calculating priority:', error);
      }
    }
    return null;
  };

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            {/* Mobile menu button */}
            <div className="flex items-center md:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
                onClick={onMobileMenuToggle}
              >
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard">
                <img
                  className="h-12 w-auto"
                  src="/spareswhite.png"
                  alt="ZoneXSpares Logo"
                />
              </Link>
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 flex items-center justify-center px-2 lg:ml-6 lg:justify-end">
            <div className="max-w-lg w-full lg:max-w-xs">
              <label htmlFor="search" className="sr-only">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  id="search"
                  name="search"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Search orders, products, customers..."
                  type="search"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center lg:ml-4">
            {/* Notifications */}
            <div className="ml-3 relative" ref={notificationsRef}>
              <button
                className="relative p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  // No need to manually refresh since we have real-time updates
                }}
              >
                <span className="sr-only">View notifications</span>
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>

                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-xs font-medium text-white animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications dropdown */}
              {showNotifications && (
                <div className="origin-top-right absolute right-0 mt-2 w-96 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-50 max-h-96 overflow-hidden">
                  {/* Header */}
                  <div className="py-3 px-4 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAllAsRead();
                        }}
                        className="text-xs font-medium text-orange-600 hover:text-orange-500 transition-colors"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  {/* Notifications list */}
                  <div className="max-h-80 overflow-y-auto">
                    {loading ? (
                      <div className="px-4 py-6 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-500">Loading...</p>
                      </div>
                    ) : allNotifications.length > 0 ? (
                      allNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`px-4 py-3 border-b last:border-0 cursor-pointer transition-colors hover:bg-gray-50 ${
                            notification.read ? 'bg-white' : 'bg-orange-50'
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              {getNotificationIcon(notification.type)}
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {notification.title || 'Notification'}
                                    </p>
                                    {getPriorityBadge(notification)}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                    {notification.message || 'No message'}
                                  </p>
                                  <div className="flex items-center justify-between mt-1">
                                    <p className="text-xs text-gray-400">
                                      {formatNotificationTime(notification.createdAt)}
                                    </p>
                                    <span className="text-xs text-gray-400 capitalize">
                                      {notification.source === 'productRequests' ? 'Request' : 'Notification'}
                                    </span>
                                  </div>
                                </div>
                                {!notification.read && (
                                  <div className="flex-shrink-0 ml-2">
                                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-sm text-gray-500">
                        <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        No notifications yet
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="py-2">
                    <Link
                      to="/notifications"
                      className="block px-4 py-2 text-sm text-center text-orange-600 hover:bg-gray-100 transition-colors"
                      onClick={() => setShowNotifications(false)}
                    >
                      View all notifications
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile dropdown */}
            <div className="ml-3 relative" ref={profileMenuRef}>
              <div>
                <button
                  className="flex text-sm border-2 border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                >
                  <span className="sr-only">Open user menu</span>
                  {vendorProfile?.logoURL ? (
                    <img
                      className="h-8 w-8 rounded-full object-cover"
                      src={vendorProfile.logoURL}
                      alt={vendorProfile.shopName}
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-red-600 flex items-center justify-center text-white font-medium">
                      {vendorProfile?.shopName?.charAt(0) || 'Z'}
                    </div>
                  )}
                </button>
              </div>

              {/* Profile dropdown menu */}
              {showProfileMenu && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none z-50">
                  <div className="py-3 px-4">
                    <p className="text-sm font-medium text-gray-900">
                      {vendorProfile?.shopName || 'Your Shop'}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {currentUser?.email || ''}
                    </p>
                  </div>

                  <div className="py-1">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      Your Profile
                    </Link>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      Settings
                    </Link>
                  </div>

                  <div className="py-1">
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
            
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;